import { createContext, useContext, type ReactNode } from 'react';

/** The image a comment was left on, as the lightbox knows it. */
export interface ImageCommentTarget {
  src: string;
  alt: string;
}

/**
 * A remark about one spot in an image: what was typed, and where the reviewer
 * pointed. The position is normalized against the rendered image so it means
 * the same thing regardless of how the browser scaled it, and carries the
 * intrinsic pixel coordinates too when the browser has decoded the image, so
 * the receiver can crop to the region rather than guess at it.
 */
export interface ImageComment {
  text: string;
  target: ImageCommentTarget;
  /** Point as a fraction of the image, 0 to 1 from the top-left corner. */
  fraction: { x: number; y: number };
  /** The same point in the image's own pixel space, when it is known. */
  pixels?: { x: number; y: number; width: number; height: number };
}

export type ImageCommentHandler = (comment: ImageComment) => void;

const ImageCommentContext = createContext<ImageCommentHandler | null>(null);

/**
 * Opts every image viewer below it into point commenting. The design system
 * knows how to collect the remark and where it was aimed; it deliberately does
 * not know what happens next, so the host decides whether a comment becomes a
 * chat message to an agent, a review note, or nothing at all.
 *
 * Without a provider the lightbox stays a plain viewer: no crosshair, no
 * composer, no change in behavior.
 */
export function ImageCommentProvider({
  onComment,
  children,
}: {
  onComment: ImageCommentHandler;
  children: ReactNode;
}) {
  return <ImageCommentContext.Provider value={onComment}>{children}</ImageCommentContext.Provider>;
}

/** The active comment handler, or `null` when commenting is not offered here. */
export function useImageComment(): ImageCommentHandler | null {
  return useContext(ImageCommentContext);
}
